'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useToast } from '@/components/ui/use-toast';
import {
  MEDIA_GALLERY_ACCEPT_STRING,
  validateFileSize,
} from '@/constants/variables';
import type {
  FileDisplayInfo,
  FileOptions,
  FileValidationResult,
  UploadProgress,
  UploadResult,
} from '@/types/upload';
import {
  DOCUMENT_ACCEPT_STRING,
  DOCUMENT_ONLY_ACCEPT_STRING,
  validateFileSizeByType,
} from '@/validations/document-file-types';

const fileAcceptTypes = {
  mediaGallery: MEDIA_GALLERY_ACCEPT_STRING,
  autocourse: DOCUMENT_ACCEPT_STRING,
  autocourseDocumentsOnly: DOCUMENT_ONLY_ACCEPT_STRING,
};

function getFileTypeDisplay(fileType: string | undefined): string {
  const type = fileType || 'application/octet-stream';
  const fileTypeParts = type.split('/');
  return fileTypeParts[1]?.toUpperCase() ?? 'FILE';
}

function getFileSizeInMB(size: number): string {
  return (size / (1024 * 1024)).toFixed(2);
}

// Helper function to get video duration
function getVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    if (file.type.startsWith('video/') === false) {
      resolve(0); // Not a video, return 0 duration
      return;
    }

    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };

    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      reject(new Error('Could not load video metadata'));
    };

    video.src = URL.createObjectURL(file);
  });
}

export default function useFileUpload({
  accept = 'image/*,video/*',
  uploadFunction,
  onSuccess,
}: {
  accept?: string;
  uploadFunction: (file: File, signal?: AbortSignal) => Promise<string>;
  onSuccess?: () => Promise<void>;
}) {
  const translate = useTranslations();
  const { toast } = useToast();
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});
  const abortControllersRef = useRef<Record<string, AbortController>>({});

  const isUploading = useMemo(() => {
    return Object.values(uploadProgress).some(progress => progress > 0 && progress < 100);
  }, [uploadProgress]);

  const updateProgress = useCallback((fileName: string, progress: number) => {
    setUploadProgress(prev => ({
      ...prev,
      [fileName]: progress,
    }));
  }, []);

  const isFileUploading = useCallback((fileName: string): boolean => {
    const progress = uploadProgress[fileName] || 0;
    return progress > 0 && progress < 100;
  }, [uploadProgress]);

  const getFileInfo = useCallback((file: File | undefined | null): FileDisplayInfo => {
    if (file === null || file === undefined) {
      return { fileTypeDisplay: 'FILE', fileSize: '0' };
    }

    return {
      fileTypeDisplay: getFileTypeDisplay(file.type),
      fileSize: getFileSizeInMB(file.size),
    };
  }, []);

  const cancelUpload = useCallback((fileName: string) => {
    const controller = abortControllersRef.current[fileName];
    if (controller !== undefined) {
      controller.abort();
      delete abortControllersRef.current[fileName];
      setUploadProgress((prev) => {
        const next = { ...prev };
        delete next[fileName];
        return next;
      });
    }
  }, []);

  const openFilePicker = useCallback(async (options: FileOptions = {}) => {
    const { multiple = true, accept: customAccept } = options;
    const effectiveAccept = customAccept || accept;

    return new Promise<FileList | null>((resolve) => {
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = effectiveAccept;
      fileInput.multiple = multiple;

      let onFocusFn: () => void;

      const cleanupInput = () => {
        document.body.removeEventListener('focus', onFocusFn);
        window.removeEventListener('focus', onFocusFn);
      };

      onFocusFn = () => {
        setTimeout(() => {
          if (fileInput.files === null || fileInput.files.length === 0) {
            resolve(null);
            cleanupInput();
          }
        }, 300);
      };

      fileInput.addEventListener('change', (event) => {
        const files = (event.target as HTMLInputElement).files;
        resolve(files);
        cleanupInput();
      });

      fileInput.addEventListener('cancel', () => {
        resolve(null);
        cleanupInput();
      });

      document.body.addEventListener('focus', onFocusFn);
      window.addEventListener('focus', onFocusFn);

      fileInput.click();
    });
  }, [accept]);

  const validateFile = useCallback(async (
    file: File,
    options: Pick<FileOptions, 'maxSizeMB' | 'allowedTypes' | 'minSizeMB' | 'maxVideoDurationSeconds' | 'useTypeSpecificSizeLimit'> = {},
  ): Promise<FileValidationResult> => {
    const {
      maxSizeMB,
      allowedTypes = [],
      minSizeMB = 0,
      maxVideoDurationSeconds = 5, // Default to 5 seconds if not specified
      useTypeSpecificSizeLimit = false,
    } = options;

    // Check file size - use type-specific limits for documents if enabled
    if (useTypeSpecificSizeLimit === true) {
      // Use type-specific validation for document files (PDF, Word, Excel, PowerPoint)
      const typeSpecificValidation = validateFileSizeByType({
        name: file.name,
        size: file.size,
        type: file.type,
      });

      if (typeSpecificValidation.valid === false) {
        return {
          valid: false,
          success: false,
          error: typeSpecificValidation.error,
          message: typeSpecificValidation.error || '',
        };
      }
    } else {
      // Use generic size validation with provided or default limit
      const effectiveMaxSizeMB = maxSizeMB ?? 100;
      const sizeValidation = validateFileSize(file, effectiveMaxSizeMB, minSizeMB);
      if (sizeValidation.valid === false) {
        return {
          valid: false,
          success: false,
          error: sizeValidation.error,
          message: sizeValidation.error || '',
        };
      }
    }

    // Check file type if allowed types are specified
    if (allowedTypes.length > 0) {
      const fileType = file.type;
      const fileExtension = file.name.split('.').pop()?.toLowerCase();

      const isAllowedMimeType = allowedTypes.some((type) => {
        if (type.endsWith('/*')) {
          const category = type.split('/')[0];
          return fileType.startsWith(`${category}/`);
        }
        return type === fileType;
      });

      const isAllowedExtension = allowedTypes.some((type) => {
        if (type.startsWith('.') && fileExtension !== undefined) {
          return type.substring(1) === fileExtension;
        }
        return false;
      });

      if (isAllowedMimeType === false && isAllowedExtension === false) {
        const message = `File type "${file.type}" is not allowed. Allowed types: ${allowedTypes.join(', ')}`;
        return {
          valid: false,
          success: false,
          error: message,
          message,
        };
      }
    }

    // Check video duration limit
    if (file.type.startsWith('video/') === true) {
      try {
        const duration = await getVideoDuration(file);

        if (duration > maxVideoDurationSeconds) {
          const message = `Video duration (${duration.toFixed(1)} seconds) exceeds the maximum allowed (${maxVideoDurationSeconds} seconds)`;
          return {
            valid: false,
            success: false,
            error: message,
            message,
          };
        }
      } catch (error) {
        console.error('Error checking video duration:', error);
        // If we can't determine duration, we should still allow the upload
      }
    }

    return {
      valid: true,
      success: true,
      message: 'File validation successful',
    };
  }, []);

  const uploadFile = useCallback(async (file: File): Promise<string> => {
    try {
      const controller = new AbortController();
      abortControllersRef.current[file.name] = controller;
      updateProgress(file.name, 0);
      return await uploadFunction(file, controller.signal);
    } catch (error) {
      delete abortControllersRef.current[file.name];
      throw error;
    } finally {
      setUploadProgress((prev) => {
        const next = { ...prev };
        delete next[file.name];
        return next;
      });
    }
  }, [uploadFunction, updateProgress]);

  const uploadHandler = useCallback(async (options: FileOptions = {}): Promise<UploadResult> => {
    const files = await openFilePicker(options);

    if (files === null || files.length === 0) {
      return {
        success: false,
        successCount: 0,
        errorCount: 0,
        message: translate('upload.status.noFilesSelected'),
      };
    }

    const filesArray = Array.from(files);
    const results: Array<{ file: File; success: boolean; error?: Error | string }> = [];

    // Show initial toast
    toast({
      title: translate('upload.status.uploadStarted'),
      description: translate('upload.status.processing', { count: String(filesArray.length) }),
      variant: 'success',
    });

    for (const file of filesArray) {
      try {
        // Validate the file before uploading
        const validationResult = await validateFile(file, {
          maxSizeMB: options.maxSizeMB ?? 25, // Default to 25MB limit if not specified
          minSizeMB: options.minSizeMB,
          allowedTypes: options.allowedTypes,
          maxVideoDurationSeconds: options.maxVideoDurationSeconds ?? 5, // Default to 5 seconds
          useTypeSpecificSizeLimit: options.useTypeSpecificSizeLimit ?? false,
        });

        if (validationResult.valid === false) {
          // Show validation error toast
          toast({
            title: translate('upload.status.validationError', { filename: file.name }),
            description: validationResult.message,
            variant: 'error',
          });

          throw new Error(validationResult.message);
        }

        await uploadFile(file);
        results.push({ file, success: true });
      } catch (caughtError) {
        // Normalize error to Error | string type
        const error: Error | string = caughtError instanceof Error
          ? caughtError
          : String(caughtError);

        results.push({ file, success: false, error });

        // Don't show duplicate toasts for validation errors (already shown above)
        const errorMessage = error instanceof Error ? error.message : error;
        if (errorMessage.includes('exceeds the maximum allowed') === false) {
          toast({
            title: translate('upload.status.failedToUpload', { filename: file.name }),
            description: errorMessage,
            variant: 'error',
          });
        }
      }
    }

    const successCount = results.filter(r => r.success === true).length;
    const errorCount = filesArray.length - successCount;

    // Show completion toast
    if (successCount > 0) {
      toast({
        title: translate('upload.status.uploadComplete'),
        description: translate('upload.status.uploadResults', { successCount: successCount.toString(), errorCount: errorCount.toString() }),
        variant: successCount === filesArray.length ? 'success' : 'error',
      });
    } else if (errorCount > 0) {
      // Only show this if we haven't shown individual error toasts for validation errors
      const hasValidationErrors = results.some((r) => {
        if (r.success === false && r.error !== undefined) {
          const message = r.error instanceof Error ? r.error.message : r.error;
          return message.includes('exceeds the maximum allowed');
        }
        return false;
      });

      if (hasValidationErrors === false) {
        // Find the first error to display
        const firstError = results.find(r => r.success === false && r.error !== undefined);
        let errorMessage = translate('upload.status.genericFailure', { count: String(errorCount) });
        if (firstError?.error !== undefined) {
          errorMessage = firstError.error instanceof Error
            ? firstError.error.message
            : firstError.error;
        }

        toast({
          title: translate('upload.status.uploadFailed'),
          description: errorMessage,
          variant: 'error',
        });
      }
    }

    const result = {
      success: successCount > 0,
      successCount,
      errorCount,
      results,
      message: translate('upload.status.uploadResults', { successCount: successCount.toString(), errorCount: errorCount.toString() }),
    };

    if (result.success === true && onSuccess !== undefined) {
      await onSuccess();
    }

    return result;
  }, [translate, toast, onSuccess, openFilePicker, uploadFile, validateFile]);

  return {
    uploadProgress,
    isUploading,
    isFileUploading,
    getFileInfo,
    openFilePicker,
    uploadFile,
    cancelUpload,
    uploadHandler,
    validateFile,
    updateProgress,
    fileAcceptTypes,
  };
}

export { fileAcceptTypes, getVideoDuration };
